/**
 * UserManagementPanel — admin-only view of all dashboard users.
 *
 * Shows: username, role, active/disabled state, created date, last login.
 * Actions: enable/disable a user (toggle is_active in user_management.users).
 *
 * Backend: /users/list and /users/toggle (both require admin role).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, useAuth } from '../../auth/AuthContext';
import './UserManagementPanel.css';

interface UserRow {
  user_id: string;
  username: string;
  role: 'admin' | 'user';
  is_active: boolean;
  approved: boolean;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

export default function UserManagementPanel() {
  const { session } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/users/list');
      if (r.ok) {
        const data = await r.json();
        setUsers(data.users || []);
      }
    } catch {
      /* keep state */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleToggle = async (user: UserRow) => {
    if (busy) return;
    if (user.user_id === session?.user_id) {
      alert('You cannot disable your own account.');
      return;
    }
    const action = user.is_active ? 'disable' : 'enable';
    if (
      !window.confirm(
        `${action.charAt(0).toUpperCase() + action.slice(1)} user "${user.username}"?`,
      )
    ) {
      return;
    }

    setBusy(user.user_id);
    try {
      const params = new URLSearchParams({
        user_id: user.user_id,
        is_active: user.is_active ? '0' : '1',
      });
      const r = await apiFetch(`/users/toggle?${params}`, { method: 'POST' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        alert(`Failed: ${j.detail || r.status}`);
      } else {
        await refresh();
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setBusy(null);
    }
  };

  const handleApprove = async (user: UserRow) => {
    if (busy) return;
    if (
      !window.confirm(
        `Approve user "${user.username}"? They will be able to sign in and create API keys.`,
      )
    ) {
      return;
    }

    setBusy(user.user_id);
    try {
      const params = new URLSearchParams({
        user_id: user.user_id,
        approved: '1',
      });
      const r = await apiFetch(`/users/approve?${params}`, { method: 'POST' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        alert(`Failed: ${j.detail || r.status}`);
      } else {
        await refresh();
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (filter === 'active' && !u.is_active) return false;
      if (filter === 'disabled' && u.is_active) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!u.username.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [users, search, filter]);

  const stats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((u) => u.is_active).length,
      disabled: users.filter((u) => !u.is_active).length,
      admins: users.filter((u) => u.role === 'admin').length,
      pending: users.filter((u) => u.is_active && !u.approved).length,
    }),
    [users],
  );

  return (
    <div className="um-root">
      <div className="um-header">
        <div>
          <div className="um-title">User Management</div>
          <div className="um-sub">
            View all dashboard users and their last login time
          </div>
        </div>
        <button className="um-refresh" onClick={refresh}>
          ↻ Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="um-stats">
        <div className="um-stat-card">
          <div className="um-stat-val">{stats.total}</div>
          <div className="um-stat-key">Total Users</div>
        </div>
        <div className="um-stat-card">
          <div className="um-stat-val um-active">{stats.active}</div>
          <div className="um-stat-key">Active</div>
        </div>
        <div className="um-stat-card">
          <div className="um-stat-val" style={{ color: '#b7791f' }}>
            {stats.pending}
          </div>
          <div className="um-stat-key">Pending</div>
        </div>
        <div className="um-stat-card">
          <div className="um-stat-val um-disabled">{stats.disabled}</div>
          <div className="um-stat-key">Disabled</div>
        </div>
        <div className="um-stat-card">
          <div className="um-stat-val um-admin">{stats.admins}</div>
          <div className="um-stat-key">Admins</div>
        </div>
      </div>

      {/* Controls */}
      <div className="um-controls">
        <input
          className="um-search"
          placeholder="Search by username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="um-filter-group">
          {(['all', 'active', 'disabled'] as const).map((f) => (
            <button
              key={f}
              className={`um-filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="um-table-wrap">
        {loading ? (
          <div className="um-empty">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="um-empty">No users match the filter</div>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Login</th>
                <th className="um-action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSelf = u.user_id === session?.user_id;
                return (
                  <tr
                    key={u.user_id}
                    className={u.is_active ? '' : 'um-row-disabled'}
                  >
                    <td className="um-username">
                      {u.username}
                      {isSelf && <span className="um-you-badge">you</span>}
                    </td>
                    <td>
                      <span className={`um-role-badge um-role-${u.role}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      {!u.is_active ? (
                        <span className="um-status um-status-disabled">
                          ● Disabled
                        </span>
                      ) : u.approved ? (
                        <span className="um-status um-status-active">
                          ● Active
                        </span>
                      ) : (
                        <span
                          className="um-status"
                          style={{ color: '#b7791f' }}
                        >
                          ● Pending approval
                        </span>
                      )}
                    </td>
                    <td className="um-time">{formatDate(u.created_at)}</td>
                    <td className="um-time">
                      {u.last_login_at ? (
                        formatDate(u.last_login_at)
                      ) : (
                        <span className="um-never">never</span>
                      )}
                    </td>
                    <td className="um-action-col">
                      {u.is_active && !u.approved && (
                        <button
                          className="um-toggle-btn um-toggle-enable"
                          onClick={() => handleApprove(u)}
                          disabled={busy === u.user_id}
                          style={{ marginRight: 6 }}
                        >
                          {busy === u.user_id ? '...' : 'Approve'}
                        </button>
                      )}
                      <button
                        className={`um-toggle-btn ${u.is_active ? 'um-toggle-disable' : 'um-toggle-enable'}`}
                        onClick={() => handleToggle(u)}
                        disabled={busy === u.user_id || isSelf}
                        title={isSelf ? 'You cannot disable yourself' : ''}
                      >
                        {busy === u.user_id
                          ? '...'
                          : u.is_active
                            ? 'Disable'
                            : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
